// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent, validateSplitsBalance } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import { buildCurrencyExchangeSplits } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import type { Split, TransactionPostedPayload, TransactionDeletedPayload, Transaction, Account } from '../../types.js';

interface SplitInput {
  id: string;
  accountId: string;
  amount: string;
  memo: string;
}

interface TransactionFormProps {
  defaultAccountId?: string;
  editTransaction?: Transaction;
  worker: Worker | null;
  keyData: ArrayBuffer | null;
  onClose: () => void;
}

function parseMinorUnits(str: string, currency: string): number {
  try {
    if (!/^[\d.+\-*/() ]+$/.test(str)) return 0;
    const result = Function('"use strict"; return (' + str + ')')() as number;
    if (typeof result !== 'number' || isNaN(result)) return 0;
    const decimals = getCurrencyDecimalPlaces(currency);
    return Math.round(result * Math.pow(10, decimals));
  } catch {
    return 0;
  }
}

function formatMajorAmount(minorUnits: number, currency: string): string {
  const decimals = getCurrencyDecimalPlaces(currency);
  return (minorUnits / Math.pow(10, decimals)).toFixed(decimals);
}

function parseSmartDate(input: string): string {
  const today = new Date();
  if (input === 't' || input === 'today') return today.toISOString().slice(0, 10);
  if (input === 'y' || input === 'yesterday') {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }
  const relMatch = input.match(/^([+-])(\d+)$/);
  if (relMatch) {
    const [, sign, daysStr] = relMatch;
    if (sign !== undefined && daysStr !== undefined) {
      const d = new Date(today);
      d.setDate(d.getDate() + parseInt(sign + daysStr, 10));
      return d.toISOString().slice(0, 10);
    }
  }
  return input;
}

const inputCls = 'bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none placeholder-text-subtle w-full';
const labelCls = 'block text-sm text-text-muted mb-1';

export function TransactionForm({
  defaultAccountId,
  editTransaction,
  worker,
  keyData,
  onClose,
}: TransactionFormProps) {
  const { t } = useTranslation(['transactions', 'accounting', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const accounts = [...ledger.accounts.values()].filter((a) => !a.placeholder);
  const accountMap = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(editTransaction?.date ?? today);
  const [dateInput, setDateInput] = useState(editTransaction?.date ?? today);
  const [num, setNum] = useState(editTransaction?.num ?? '');
  const [description, setDescription] = useState(editTransaction?.description ?? '');
  const [notes, setNotes] = useState(editTransaction?.notes ?? '');
  const [splits, setSplits] = useState<SplitInput[]>(() => {
    if (editTransaction) {
      return editTransaction.splits.map((s) => {
        const acct = ledger.accounts.get(s.accountId);
        const cur = acct?.currency ?? 'USD';
        return { id: s.splitId, accountId: s.accountId, amount: formatMajorAmount(s.amount, cur), memo: s.memo };
      });
    }
    return [
      { id: uuidv4(), accountId: defaultAccountId ?? '', amount: '', memo: '' },
      { id: uuidv4(), accountId: '', amount: '', memo: '' },
    ];
  });
  const [crossRateInput, setCrossRateInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Per-split resolved account
  function splitAccount(split: SplitInput): Account | undefined {
    return accountMap.get(split.accountId);
  }

  // Detect cross-currency scenario: exactly 2 splits, different currencies, both accounts known
  const splitCurrencies = splits.map((s) => splitAccount(s)?.currency ?? null);
  const firstCurrency = splitCurrencies[0] ?? null;
  const isCrossCurrency = splits.length === 2 &&
    splitCurrencies[0] !== null &&
    splitCurrencies[1] !== null &&
    splitCurrencies[0] !== splitCurrencies[1];

  const fromCurrency = isCrossCurrency ? (splitCurrencies[0] ?? 'USD') : null;
  const toCurrency = isCrossCurrency ? (splitCurrencies[1] ?? 'USD') : null;

  // Unique currencies across all splits (for multi-split multi-currency)
  const uniqueCurrencies = new Set(splitCurrencies.filter((c): c is string => c !== null));
  const hasAnyCurrencyMismatch = uniqueCurrencies.size > 1;

  const allDescriptions = useMemo(() => {
    const descs = new Set<string>();
    for (const tx of ledger.transactions.values()) {
      if (tx.description) descs.add(tx.description);
    }
    return [...descs].sort();
  }, [ledger.transactions]);

  const [showDescSuggestions, setShowDescSuggestions] = useState(false);
  const descSuggestions = description
    ? allDescriptions.filter((d) => d.toLowerCase().includes(description.toLowerCase()) && d !== description)
    : [];

  function updateSplit(idx: number, field: keyof SplitInput, value: string) {
    setSplits((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  }

  function addSplit() {
    setSplits((prev) => [...prev, { id: uuidv4(), accountId: '', amount: '', memo: '' }]);
  }

  function removeSplit(idx: number) {
    if (splits.length <= 2) return;
    setSplits((prev) => prev.filter((_, i) => i !== idx));
  }

  // For same-currency: check if all splits sum to 0
  function totalMinor(): number {
    const cur = firstCurrency ?? 'USD';
    return splits.reduce((sum, s) => {
      const acct = splitAccount(s);
      return sum + parseMinorUnits(s.amount, acct?.currency ?? cur);
    }, 0);
  }

  const isBalanced = (): boolean => {
    if (isCrossCurrency) {
      return splits[0]?.amount !== '' && splits[1]?.amount !== '' && crossRateInput !== '';
    }
    if (!hasAnyCurrencyMismatch) {
      return totalMinor() === 0 && splits.every((s) => s.amount !== '');
    }
    // Multi-split multi-currency: group by currency and check each sums to 0
    const byCurrency = new Map<string, number>();
    for (const s of splits) {
      const acct = splitAccount(s);
      if (!acct) continue;
      const cur = acct.currency;
      const prev = byCurrency.get(cur) ?? 0;
      byCurrency.set(cur, prev + parseMinorUnits(s.amount, cur));
    }
    return [...byCurrency.values()].every((v) => v === 0);
  };

  function autoCompleteFromPayee(desc: string) {
    const matches = [...ledger.transactions.values()]
      .filter((tx) => tx.description === desc && !tx.voided)
      .sort((a, b) => b.date.localeCompare(a.date));
    const last = matches[0];
    if (last && last.splits.length >= 2) {
      setNotes(last.notes);
      setSplits(
        last.splits.map((s) => {
          const acct = ledger.accounts.get(s.accountId);
          const cur = acct?.currency ?? 'USD';
          return { id: uuidv4(), accountId: s.accountId, amount: formatMajorAmount(s.amount, cur), memo: s.memo };
        }),
      );
    }
  }

  function handleDateBlur() {
    const resolved = parseSmartDate(dateInput);
    setDate(resolved);
    setDateInput(resolved);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!date) { setError(t('form.dateRequired')); return; }
    if (!description.trim()) { setError(t('form.descriptionRequired')); return; }
    for (const s of splits) {
      if (!s.accountId) { setError(t('form.splitsNeedAccount')); return; }
    }

    let builtSplits: Split[];

    if (isCrossCurrency && splits.length === 2) {
      // Build 4-split cross-currency transaction
      const split0 = splits[0];
      const split1 = splits[1];
      if (!split0 || !split1) { setError(t('form.couldNotResolveSplits')); return; }
      const fromAcct = splitAccount(split0);
      const toAcct = splitAccount(split1);
      if (!fromAcct || !toAcct) { setError(t('form.couldNotResolveAccounts')); return; }

      const fromAmount = parseMinorUnits(split0.amount, fromAcct.currency);
      const toAmount = parseMinorUnits(split1.amount, toAcct.currency);

      if (fromAmount === 0 || toAmount === 0) { setError(t('form.enterBothAccountAmounts')); return; }

      try {
        builtSplits = buildCurrencyExchangeSplits(
          fromAcct,
          toAcct,
          Math.abs(fromAmount),
          Math.abs(toAmount),
          split0.memo,
          accounts,
          uuidv4,
        );
      } catch (err) {
        setError(
          err instanceof Error
            ? t('form.tradingAccountsNeeded', { message: err.message, fromCurrency: fromAcct.currency, toCurrency: toAcct.currency })
            : t('form.buildCrossCurrencyFailed'),
        );
        return;
      }
    } else {
      builtSplits = splits.map((s) => {
        const acct = splitAccount(s);
        const cur = acct?.currency ?? firstCurrency ?? 'USD';
        const amount = parseMinorUnits(s.amount, cur);
        return {
          splitId: s.id,
          accountId: s.accountId,
          amount,
          quantity: amount,
          reconcileState: 'n' as const,
          reconcileDate: null,
          memo: s.memo,
          action: '',
        };
      });

      try {
        validateSplitsBalance(builtSplits);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('errors:transactionUnbalanced'));
        return;
      }
    }

    setSaving(true);
    try {
      const trimmedNum = num.trim();
      const currency = firstCurrency ?? 'USD';
      const baseSeq = ledger.lastSequence;

      if (editTransaction) {
        const deletePayload: TransactionDeletedPayload = { transactionId: editTransaction.id };
        await encryptAndPush(await buildEvent('TransactionDeleted', deletePayload, baseSeq + 1));
      }

      const payload: TransactionPostedPayload = {
        transactionId: editTransaction?.id ?? uuidv4(),
        date,
        dateEntered: new Date().toISOString(),
        description: description.trim(),
        ...(trimmedNum ? { num: trimmedNum } : {}),
        notes: notes.trim(),
        currency,
        splits: builtSplits,
      };

      await encryptAndPush(await buildEvent('TransactionPosted', payload, editTransaction ? baseSeq + 2 : baseSeq + 1));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:saveTransaction'));
    } finally {
      setSaving(false);
    }
  }

  const balanced = isBalanced();
  const total = hasAnyCurrencyMismatch ? 0 : totalMinor();
  const firstCur = firstCurrency ?? 'USD';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-text">
              {editTransaction ? t('form.editTransaction') : t('form.newTransaction')}
            </h2>
            <button onClick={onClose} className="text-text-muted hover:text-text text-xl leading-none">&times;</button>
          </div>

          {error && (
            <div className="border border-negative text-negative px-3 py-2 mb-4 text-sm">{error}</div>
          )}

          {isCrossCurrency && (
            <div className="border border-accent/40 bg-accent/5 px-3 py-2 mb-4 text-xs text-text-muted">
              {t('form.crossCurrencyNoteDetailed', { fromCurrency, toCurrency })}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>{t('form.date')}</label>
                <input
                  type="text"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  onBlur={handleDateBlur}
                  className={inputCls}
                  placeholder={t('form.datePlaceholder')}
                />
              </div>
              <div>
                <label className={labelCls}>{t('form.numRef')}</label>
                <input
                  type="text"
                  value={num}
                  onChange={(e) => setNum(e.target.value)}
                  className={`${inputCls} font-mono`}
                  placeholder={t('form.numPlaceholder')}
                />
              </div>
              <div />
            </div>

            <div className="relative">
              <label className={labelCls}>{t('form.descriptionPayee')}</label>
              <input
                type="text"
                value={description}
                onChange={(e) => { setDescription(e.target.value); setShowDescSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowDescSuggestions(false), 150)}
                className={inputCls}
                placeholder={t('form.payeePlaceholder')}
                autoFocus
              />
              {showDescSuggestions && descSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-0 bg-bg-surface border border-border-strong max-h-40 overflow-y-auto">
                  {descSuggestions.slice(0, 8).map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        setDescription(d);
                        setShowDescSuggestions(false);
                        autoCompleteFromPayee(d);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-text-muted hover:bg-bg-elevated hover:text-text"
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>{t('form.notes')} <span className="text-text-subtle">{t('form.optional')}</span></label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Splits */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-text-muted">{t('form.splits')}</label>
                {!isCrossCurrency && (
                  <span className={`text-xs font-mono px-2 py-0.5 border ${balanced ? 'border-positive text-positive' : 'border-negative text-negative'}`}>
                    {balanced ? t('form.balanced') : hasAnyCurrencyMismatch ? t('form.multiCurrency') : t('form.offBy', { amount: `${formatMajorAmount(Math.abs(total), firstCur)} ${firstCur}` })}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {splits.map((split, idx) => {
                  const acct = splitAccount(split);
                  const cur = acct?.currency ?? null;
                  return (
                    <div key={split.id} className="flex items-center gap-2">
                      <select
                        value={split.accountId}
                        onChange={(e) => updateSplit(idx, 'accountId', e.target.value)}
                        className="flex-1 bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                      >
                        <option value="">{t('form.selectAccount')}</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                      <div className="relative">
                        <input
                          type="text"
                          value={split.amount}
                          onChange={(e) => updateSplit(idx, 'amount', e.target.value)}
                          className="w-32 bg-bg border border-border text-text text-sm text-right px-2 py-1.5 focus:border-accent focus:outline-none font-mono"
                          placeholder={getCurrencyDecimalPlaces(cur ?? 'USD') === 0 ? '0' : '0.00'}
                          title={t('form.expressionHint')}
                        />
                        {cur && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-text-subtle pointer-events-none" style={{ right: '6px' }}>
                          </span>
                        )}
                      </div>
                      {cur && <span className="text-xs text-text-subtle font-mono w-8 shrink-0">{cur}</span>}
                      <input
                        type="text"
                        value={split.memo}
                        onChange={(e) => updateSplit(idx, 'memo', e.target.value)}
                        className="w-20 bg-bg border border-border text-text text-sm px-2 py-1.5 focus:border-accent focus:outline-none"
                        placeholder={t('form.memo')}
                      />
                      {splits.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeSplit(idx)}
                          className="text-text-subtle hover:text-negative text-lg leading-none px-1"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={addSplit}
                className="mt-2 text-sm text-accent hover:text-accent-hover"
              >
                + {t('form.addSplit')}
              </button>

              {isCrossCurrency && fromCurrency && toCurrency && (
                <div className="mt-3 p-3 bg-bg border border-border-strong text-xs text-text-muted">
                  <p className="mb-1 font-medium text-text">{t('form.exchangeRateReference')}</p>
                  <div className="flex items-center gap-2">
                    <span>{t('form.rateEquals', { currency: fromCurrency })}</span>
                    <input
                      type="number"
                      step="any"
                      value={crossRateInput}
                      onChange={(e) => setCrossRateInput(e.target.value)}
                      className="w-28 bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none font-mono"
                      placeholder={t('form.ratePlaceholder')}
                    />
                    <span>{toCurrency}</span>
                  </div>
                  <p className="mt-1 text-text-subtle">
                    {t('form.enterBothAmounts')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving || !balanced}
                className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-40 transition-colors"
              >
                {saving ? t('common:actions.saving') : t('form.saveTransaction')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
